import * as React from 'react';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import SearchService from '@app/components/SearchService/SearchService';
import CategoryItem from '@app/components/CategoryItem/CategoryItem';
import { eventBus } from '@app/services/eventBus';
import { isIEDetect } from '@app/helpers/utils';
import { connect, useSelector } from 'react-redux';
import { AppState } from '@app/store';
import { openCategory, setPopular } from '@app/store/catalogue/thunks';
import cn from 'classnames';
import CatalogMobileTitleNavigation from '@app/components/CatalogMobileTitleNavigation/CatalogMobileTitleNavigation';
import './style.scss';
import { CategoryListProps } from './CategoryListProps';
import { FROM_SUPERAPP } from '@app/constants/fromParams';
import { useFocusOnLocationChange } from '@app/hooks/useFocusOnLocationChange';
import { MetricParam, useSpatz } from '@app/hooks/useSpatz';
import { getCategoryById } from '@app/store/catalogue/selectors';

const CategoryList: React.FC<CategoryListProps> = ({
  catalogue,
  openCategory,
  lastOpenedCategory,
  isSuperApp,
  windowWidth,
  headerHeight,
  windowHeight,
  animationDisabled,
  isPopular,
}) => {
  const [marginCat, setMarginCat] = useState<React.ReactText | undefined>(
    undefined
  );
  const firstCategoryRef = useRef(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const parentId = lastOpenedCategory?.parentId;
  const parentCategory = useSelector(getCategoryById(parentId));
  const { sendSpatzStats } = useSpatz({
    Services: { catalog: { Главная: {} } },
  });

  const [hasViewedCatalog, setHasViewedCatalog] = useState(false);
  let rootScrollable = React.createRef<HTMLDivElement>();
  // При смене типа каталога скролим первую колонку на самый верх
  eventBus.addHandler((event, ref) => {
    if (event === 'catalogTypeUpdated') {
      if (ref && ref.current) {
        ref.current.scrollTop = 0;
      }
    }
  }, rootScrollable);

  const onClickHandler = () => {
    if (lastOpenedCategory && parentCategory) {
      const data: MetricParam = {
        click: {
          'Название раздела': lastOpenedCategory.title,
          parent: parentCategory.title,
        },
      };
      sendSpatzStats(data);
    }
    openCategory(lastOpenedCategory?.parentId);
  };

  function backPath() {
    const pathArr = lastOpenedCategory?.routePath.split('/');
    const path = pathArr?.slice(0, pathArr.length - 2);
    const url = path?.join('/') + '/';
    return url;
  }

  useEffect(() => {
    if (!hasViewedCatalog) {
      const data: MetricParam = {
        'Просмотр раздела': { view: 'Каталог услуг' },
      };
      sendSpatzStats(data);
      setHasViewedCatalog(true);
    }
    window.scrollTo(0, 0);
  }, [lastOpenedCategory, sendSpatzStats, hasViewedCatalog]);

  function setStyle(n: number) {
    let style = {
      height: windowWidth >= 1024 ? `${windowHeight}px` : 'auto',
      left: '0px',
      boxShadow: 'none',
    };
    const boxShadow = '-12px 0 10px -12px rgba(25, 24, 59, 0.12)';
    if (windowWidth >= 1024) {
      let posLeft;
      const colWidth = 295; //ширина колонки вместе с отступом
      const colFirstWidth = 341; //ширина первой колонки вместе с отступом
      const positionLeft = 253; // сдвиг второй колонки
      const num = catalogue!.levels.length; //количество колонок
      if (num > 2) {
        let selfShadow = boxShadow;
        if (num - 2 !== n) {
          selfShadow = 'none';
        }
        style = {
          ...style,
          left: '40px',
          boxShadow: selfShadow,
        };
      } else {
        if (!displayCategories()) {
          posLeft = colFirstWidth + colWidth * (n + 1) + 'px';
          const content = document.getElementsByClassName('catalogue-app')[0];
          const wContent =
            windowWidth >= 1280
              ? content.clientWidth - 290
              : content.clientWidth; //ширина контентной области
          if (colWidth * (num + 1) < wContent) {
            style = {
              ...style,
              left: posLeft,
              boxShadow: 'none',
            };
          } else {
            posLeft = positionLeft + 'px';
            style = {
              ...style,
              boxShadow,
              left: posLeft,
            };
          }
        } else {
          posLeft = positionLeft + 'px';
          style = {
            ...style,
            boxShadow,
            left: posLeft,
          };
        }
      }
      style = {
        ...style,
        left: style.left,
      };
    }
    return style;
  }

  const displayCategories = useCallback(() => {
    return (
      !lastOpenedCategory ||
      (lastOpenedCategory && lastOpenedCategory.elementsType !== 'services') ||
      windowWidth >= 1024 ||
      (!isPopular &&
        windowWidth < 1024 &&
        lastOpenedCategory &&
        lastOpenedCategory.elementsType !== 'services')
    );
  }, [lastOpenedCategory, windowWidth, isPopular]);

  useEffect(() => {
    function setWidth() {
      if (windowWidth >= 1024) {
        const colWidth = 295; //ширина колонки вместе с отступом
        const colFirstWidth = 341; //ширина первой колонки вместе с отступом
        const posLeft = 253; // сдвиг второй колонки
        const num = catalogue!.levels.length; //количество колонок
        if (num > 2) {
          setMarginCat(40);
        } else {
          if (!displayCategories()) {
            setMarginCat('auto');
          } else {
            const mCatColumn: number = colWidth + posLeft - colFirstWidth;
            setMarginCat(num > 1 ? mCatColumn : 'auto');
          }
        }
      } else {
        setMarginCat('auto');
      }
    }

    setWidth();
  }, [
    catalogue,
    lastOpenedCategory,
    windowHeight,
    windowWidth,
    displayCategories,
  ]);

  useFocusOnLocationChange(catalogue, windowWidth);

  return (
    <div
      className="mos-catalog_category-wrap mos-catalog_position-sticky"
      style={{
        marginRight: marginCat,
        top: isIEDetect() ? 0 : headerHeight,
        maxHeight: windowWidth >= 1024 ? `${windowHeight}px` : 'none',
        display:
          (windowWidth < 1023 && !isPopular) || windowWidth >= 1024
            ? 'block'
            : 'none',
      }}
    >
      {(windowWidth > 1023 ||
        (!isPopular &&
          lastOpenedCategory &&
          lastOpenedCategory.elementsType !== 'services')) && (
        <div className="mos-catalog_category-content">
          {windowWidth > 1023 && <SearchService ref={searchRef} />}
          {displayCategories() && (
            <div
              className="mos-catalog_category-list-first"
              id="mos-catalog_category-list"
              ref={firstCategoryRef}
            >
              {lastOpenedCategory &&
                lastOpenedCategory.elementsType === 'mixed' && (
                  <div>
                    <div className="mos-catalog_category-title">
                      Каталог услуг
                    </div>
                    {!isSuperApp && (
                      <CatalogMobileTitleNavigation></CatalogMobileTitleNavigation>
                    )}
                  </div>
                )}
              <div
                className="mos-catalog_category-scroll mos-catalog_category-scroll--root"
                ref={rootScrollable}
                role="navigation"
                aria-label="Список разделов"
                tabIndex={-1}
              >
                {catalogue?.rootCategories.map((categoryId, index) => {
                  const category = catalogue.categories[categoryId];
                  if ((index > 0 && windowWidth < 1024) || windowWidth >= 1024)
                    return (
                      <CategoryItem
                        key={category.id}
                        levelFirst={true}
                        category={category}
                        isActive={catalogue.levels.indexOf(categoryId) !== -1}
                      />
                    );
                  return null;
                })}
              </div>
            </div>
          )}
          {displayCategories() &&
            catalogue?.levels?.length &&
            catalogue.levels.length > 0 && (
              <TransitionGroup component={null}>
                {catalogue.levels.map((categoryId, index) => {
                  const category = catalogue.categories[categoryId];
                  if (
                    category &&
                    category.children &&
                    category.children.length > 0
                  )
                    return (
                      <CSSTransition
                        key={categoryId}
                        timeout={1000}
                        classNames={animationDisabled ? '' : 'list-animate'}
                      >
                        <div
                          className={cn('mos-catalog_category-list', {
                            '__no-animate': animationDisabled,
                          })}
                          key={categoryId}
                          style={setStyle(index)}
                        >
                          {lastOpenedCategory &&
                            lastOpenedCategory.elementsType ===
                              'categories' && (
                              <Link
                                data-testid="backToCategoryButton"
                                to={backPath()}
                                onClick={onClickHandler}
                                aria-label={`Категория ${lastOpenedCategory.title}. Вернуться обратно`}
                                className="mos-catalog_category-title mos-catalog_category-title_back"
                              >
                                {lastOpenedCategory.title}
                              </Link>
                            )}
                          {!isSuperApp && (
                            <CatalogMobileTitleNavigation></CatalogMobileTitleNavigation>
                          )}
                          <div
                            className="mos-catalog_category-scroll mos-catalog_category-scroll--level1"
                            role="navigation"
                            aria-label="Список подразделов"
                            tabIndex={-1}
                          >
                            {category.children.map(
                              (childrenId: number, index: number) => {
                                const children =
                                  catalogue.categories[childrenId];
                                if (
                                  (index > 0 &&
                                    windowWidth < 1024 &&
                                    children) ||
                                  (windowWidth >= 1024 && children)
                                )
                                  return (
                                    <CategoryItem
                                      key={childrenId}
                                      levelFirst={false}
                                      category={children}
                                      isActive={
                                        catalogue.levels.indexOf(childrenId) !==
                                        -1
                                      }
                                    />
                                  );
                                return null;
                              }
                            )}
                          </div>
                        </div>
                      </CSSTransition>
                    );
                  return null;
                })}
              </TransitionGroup>
            )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  catalogue: state.catalogue,
  lastOpenedCategory: state.catalogue.lastOpenedCategory,
  headerHeight: state.sizes.headerHeight,
  windowHeight: state.sizes.windowHeight,
  windowWidth: state.sizes.windowWidth,
  animationDisabled: state.catalogue.animationDisabled,
  isPopular: state.catalogue.isPopular,
  isSuperApp: state.catalogue.fromRoute === FROM_SUPERAPP,
});

const dispatchProps = {
  openCategory,
  setPopular,
};

const ConnectedCategoryList = connect(
  mapStateToProps,
  dispatchProps
)(CategoryList);

export default ConnectedCategoryList;
